import { Class, CrDiff, Diff, Entity } from "../";
import { Account, Counterparty, Currency, TransactionType, Transaction, DashboardWidget, getTypeName } from "../";
import { BlobContent, Config, ExternalFile } from '../domain';

const GLOBAL_DB = {
    DB_NAME: "Global",
    WORKSPACE_STORE_NAME : "Workspace",
    SETTING_STORE_NAME: "Setting"
}

const schemas : Record<number, [Function, boolean]> = {
    1: [schemaV1, false],
    2: [schemaV2, false],
    3: [schemaV3, false],
    4: [schemaV4, true],
    5: [schemaV5, false],
}

function upgrade(db:IDBDatabase, oldVersion:number, newVersion:number|null) {
    if (!newVersion) {
        return;
    }
    let nextVersion = oldVersion + 1;    
    while (nextVersion <= newVersion) {
        const [schemaFunc, applyToGlobal] = schemas[nextVersion];
        if (!schemaFunc) {
            console.error("Database upgrade failed, schema mis configured");
        }
        const isGlobalDb = (db.name === GLOBAL_DB.DB_NAME);
        if ((applyToGlobal && isGlobalDb) 
            || (!applyToGlobal && !isGlobalDb)) {
            schemaFunc(db);
        }

        nextVersion++;
    }
}

function schemaV1(db:IDBDatabase) {
    createStore(db, getTypeName(Currency));
    createStore(db, getTypeName(Counterparty));
    createStore(db, getTypeName(Account));
    createStore(db, getTypeName(TransactionType));
    createStore(db, getTypeName(Transaction));
}

function schemaV2(db: IDBDatabase) {
    createStore(db, getTypeName(DashboardWidget));
}

function schemaV3(db: IDBDatabase) {
    createStore(db, getTypeName(Config));
}

function schemaV4(db: IDBDatabase) {
    db.createObjectStore(GLOBAL_DB.WORKSPACE_STORE_NAME, {keyPath:"uuid"});
    db.createObjectStore(GLOBAL_DB.SETTING_STORE_NAME, {keyPath:"key"});
}

function schemaV5(db: IDBDatabase) {
    createStore(db, getTypeName(BlobContent));
    createStore(db, getTypeName(ExternalFile));
}

function createStore(db:IDBDatabase, name:string) {
    db.createObjectStore(name, {keyPath: "uuid"});
}

function getMaxVersion() {
    return Object.keys(schemas).map(x => parseInt(x)).reduce(((a,b) => a > b ? a : b), 1);
}

function initIndexDB(dbname:string) {
    if (!window.indexedDB) {
        throw new Error("Your browser doesn't support a stable version of IndexedDB.")
    }
    const request = window.indexedDB.open(dbname, getMaxVersion());
    
    const promise = new Promise<IDBDatabase>((resolve, reject) => {
        request.onerror = function(event) {
            console.error("Failure event", event);
            reject(new Error("failed"));
        };
         
        let db : IDBDatabase;
        request.onsuccess = function(event) {
            db = request.result;
            resolve(db);
        };
         
        request.onupgradeneeded = function(event) {
            const oldVersion = event.oldVersion;
            const newVersion = event.newVersion;
            const target:any = event.target
            if (target["result"]) {
                upgrade(target["result"], oldVersion, newVersion);
            }
        }
    
    });

    return promise;
}

interface Database {
    getStoreNames(): String[];
    getStore<T extends Entity<T>>(type: Class<T>) : KvStore<T>;
    getStoreByName<T extends Entity<T>>(name:string) : KvStore<T>;
}

interface Dictionary<V> {
    get(key:string) : Promise<V | undefined>,
    put(key:string, value: V) : Promise<void>
    getAll(): Promise<V[]>
}

interface EntityStore<T extends Entity<T>> extends Dictionary<T> {

}

interface KvStore<T extends Entity<T>> {
    get(uuid: string) : Promise<CrDiff<T> >;
    write(uuid: string, crDiff:CrDiff<T>) : Promise<void>;
    append<J extends Diff<T>>(uuid:string, diff: J) : Promise<CrDiff<T>>;
    getAll() : Promise<CrDiff<T>[]>;
}

class KvDatabase implements Database {

    private _db:IDBDatabase|undefined = undefined;
    private _dbname: string;
    
    constructor(name:string) {
        this._dbname = name;
    }

    private initDB() {
        return initIndexDB(this._dbname);
    }

    async init() {
        this._db = await this.initDB();
    }

    getStoreNames() {
        const storeNames = [];
        if (this._db) {
            const names = this._db.objectStoreNames;
            for (let i = 0; i < names.length; i++) {
                storeNames.push(names[i]);
            }
        }
        return storeNames;
    }

    getStoreByName<T extends Entity<T>>(name:string) : IndexedDBKvListStore<T> {
        if (!this._db) {
            throw new Error("Illegal state");
        }
        return new IndexedDBKvListStore(this._db, name);
    }

    getStore<T extends Entity<T>>(type: Class<T>) : IndexedDBKvListStore<T> {
        if (!this._db) {
            throw new Error("Illegal state");
        }
        return new IndexedDBKvListStore(this._db, getTypeName(type));
    }
}



class StaticKvListStore<T extends Entity<T>> implements KvStore<T> {
    private _index: Record<string, CrDiff<T>>;
    
    constructor(index:Record<string, CrDiff<T>>) {
        this._index = index;
    }


    async get(uuid: string): Promise<CrDiff<T>> {
        return new Promise((resolve) => {
            if (this._index[uuid]) {
                resolve(this._index[uuid]);
            }
            else {
                resolve({uuid: uuid, diffs: []});
            }
        })
    }

    async write(uuid: string, crDiff: CrDiff<T>): Promise<void> {
        return new Promise((resolve) => {
            this._index[uuid] = crDiff;
            resolve();
        });
    }

    async append<J extends Diff<T>>(uuid: string, diff: J): Promise<CrDiff<T>> {
        
        const value = await this.get(uuid);
        const list = value ? value.diffs : [];
        list.push(diff);
        const appended = {uuid: uuid, diffs: list};
        await this.write(uuid, appended);
        return new Promise((resolve) => {
            resolve(appended);
        });
    }

    async getAll(): Promise<CrDiff<T>[]> {
        return new Promise((resolve) => {
            resolve(Object.values(this._index));
        })
    }
}



class IndexedDBKvListStore<T extends Entity<T>> implements KvStore<T> {
    private _db:IDBDatabase;
    private _storeName:string;

    constructor(db:IDBDatabase, storeName: string) {
        this._db = db;
        this._storeName = storeName;
    }

    async get(uuid:string) : Promise<CrDiff<T>> {

        const tx = this._db.transaction(this._storeName, 'readonly');
        const store = tx.objectStore(this._storeName);

        const promised = new Promise<CrDiff<T>>((resolve, reject) => {
            const request = store.get(uuid);
            request.onsuccess = event => resolve(request.result);
            request.onerror = event => reject(new Error(`Get failed for uuid ${uuid}`));
        });

        try {
            const value = await promised;
            return value;
            
        } catch (e) {
            return {
                uuid: uuid,
                diffs:[]
            }
        }
    }

    async write(uuid: string, crDiff:CrDiff<T>) : Promise<void> {
        const tx = this._db.transaction(this._storeName, 'readwrite');
        const store = tx.objectStore(this._storeName);

        await new Promise<IDBValidKey>((resolve, reject) => {
            const request = store.put(crDiff);
            request.onsuccess = event => resolve(request.result);
            request.onerror = event => reject(new Error(`Write failed for uuid ${uuid}`));
        });
    }

    async append<J extends Diff<T>>(uuid:string, diff: J) : Promise<CrDiff<T>> {
        let value = (await this.get(uuid));
        if (!value) {
            value = {uuid: uuid, diffs:[]};
        }
        value.diffs.push(diff);
        await this.write(uuid, value);
        return value;
    }

    async getAll() : Promise<CrDiff<T>[]> {
        const tx = this._db.transaction(this._storeName, 'readonly');
        const store = tx.objectStore(this._storeName);

        const promised = new Promise<CrDiff<T>[]>((resolve, reject) => {
            const request:IDBRequest = store.getAll();
            request.onsuccess = event => resolve(request.result);
            request.onerror = event => reject(new Error(`Getall failed for store ${this._storeName}`));
        });
        return promised;
    }
}


export {Database, KvDatabase, KvStore, StaticKvListStore, initIndexDB, GLOBAL_DB};